import { createQueryKeys } from "@lukemorales/query-key-factory"

import axios from "@/api/axios"
import { APIFn } from "@/api/utils"

import {
  Award,
  Certification,
  Interest,
  LanguageResponse,
  Profile,
  Project,
  Publication,
  Qualification,
  Skill,
  SkillEvidence,
  SubjectResponse,
  TestScore,
  WorkExperienceType,
  WorkLocationInterest,
} from "./types"

export const ProfileQueryKey = createQueryKeys("profile", {
  skills: (userName?: string) => ({
    queryKey: ["skills", userName],
  }),

  videoPortfolio: (userId?: string) => ({
    queryKey: ["video-portfolio", userId],
  }),
  listInterests: (userName?: string) => ({
    queryKey: ["list-interests", userName],
  }),
  listLanguage: (userName?: string) => ({
    queryKey: ["list-language", userName],
  }),
  listAchievements: (userName?: string) => ({
    queryKey: ["list-achievements", userName],
  }),
  listWorkExperiences: (userName?: string) => ({
    queryKey: ["list-work-experiences", userName],
  }),
  listAcademics: (userName?: string) => ({
    queryKey: ["list-academics", userName],
  }),
  profileData: (userName?: string) => ({
    queryKey: ["profile-data", userName],
  }),
  listSkillEvidences: (userName?: string) => ({
    queryKey: ["list-skill-evidences", userName],
  }),
})

type ProfileResource = {
  retrieve: APIFn<Profile, { username?: string }>
  listAchievements: APIFn<
    {
      publications: Publication[]
      awards: Award[]
      testScores: TestScore[]
      projects: Project[]
    },
    { username?: string }
  >
  listLanguages: APIFn<LanguageResponse[], { username?: string }>
  listInterests: APIFn<
    {
      preferredWorkLocations: WorkLocationInterest[]
      interests: Interest[]
      subjects: SubjectResponse[]
    },
    { username?: string }
  >
  listSkills: APIFn<Skill[], { username?: string }>
  listAcademics: APIFn<
    {
      certifications: Certification[]
      qualifications: Qualification[]
    },
    { username?: string }
  >
  retrieveVideoPortfolio: APIFn<
    { videoPortfolio: string | null },
    { userId: string }
  >
  listWorkExperiences: APIFn<WorkExperienceType[], { username?: string }>
  listSkillEvidences: APIFn<SkillEvidence, { username?: string }>
}

export const ProfileResource: ProfileResource = {
  retrieve: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listAchievements: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/achievements/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listLanguages: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/languages/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listInterests: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/interests/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listSkills: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/skills/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listAcademics: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/academics/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  retrieveVideoPortfolio: ({ urlParams, ...config }) =>
    axios.get(`/nexus/v1/profiles/public/${urlParams.userId}/`, config),

  listWorkExperiences: ({ urlParams, ...config }) =>
    axios.get(
      `/solis/v1/profile/public/work-experiences/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),

  listSkillEvidences: ({ urlParams, ...config }) =>
    axios.get(
      `/analytics/public/user-skill-portfolio/${urlParams?.username ? urlParams.username : ""}/`,
      config,
    ),
}
